import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SetPermissionsAction } from '~permissions/actions/SetPermissionsAction';
import { AdminProfileRemoveFromSiteAction } from '~src/adminProfiles/entityActions/AdminProfileRemoveFromSiteAction';
import { AdminProfileRemoveFromUserAction } from '~src/adminProfiles/entityActions/AdminProfileRemoveFromUserAction';
import { AdminProfileSiteMembershipAction } from '~src/adminProfiles/entityActions/AdminProfileSiteMembershipAction';
import { ServerUserAddToIdentityPoolAction } from '~src/authentication/components/identityPools/entityActions/ServerUserAddToIdentityPoolEntityAction';
import { ServerUserRemoveFromIdentityPoolAction } from '~src/authentication/components/identityPools/entityActions/ServerUserRemoveFromIdentityPoolEntityAction';
import { SiteUserAddToIdentityPoolAction } from '~src/authentication/components/identityPools/entityActions/SiteUserAddToIdentityPoolEntityAction';
import { SiteUserRemoveFromIdentityPoolAction } from '~src/authentication/components/identityPools/entityActions/SiteUserRemoveFromIdentityPoolEntityAction';
import { CancelBackgroundJobAction } from '~src/backgroundJob/entityActions/CancelBackgroundJobAction';
import { ViewBackgroundJobDetailsAction } from '~src/backgroundJob/entityActions/ViewBackgroundJobDetailsAction';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { GroupDatabasesEntityAction } from '~src/catalog/entityActions/databases/GroupDatabasesEntityAction';
import { UngroupDatabasesEntityAction } from '~src/catalog/entityActions/databases/UngroupDatabasesEntityAction';
import {
  DataQualityMenu,
  SetDataQualityEntityAction,
  SetDataQualityTriggerAction
} from '~src/catalog/entityActions/dataQuality/SetDataQualityEntityAction';
import { RemoveExternalContentEntityAction } from '~src/catalog/entityActions/labels/RemoveExternalContentEntityAction';
import { SetAiLabelEntityAction } from '~src/catalog/entityActions/labels/SetAiLabelEntityAction';
import {
  SetCertificationDatabaseAndTableAction, SetCertificationEntityAction
} from '~src/catalog/entityActions/labels/SetCertificationEntityAction';
import { SetCustomLabelEntityAction } from '~src/catalog/entityActions/labels/SetCustomLabelEntityAction';
import { DataLabelsSubmenu } from '~src/catalog/entityActions/labels/SetDataLabelsEntityAction';
import { SetSensitiveLabelEntityAction } from '~src/catalog/entityActions/labels/SetSensitiveLabelEntityAction';
import { DeleteLabelValueEntityAction } from '~src/catalog/entityActions/labelValue/DeleteLabelValueEntityAction';
import { RevertToDefaultLabelValueEntityAction } from '~src/catalog/entityActions/labelValue/RevertToDefaultLabelValueEntityAction';
import { SetLabelValueEntityAction } from '~src/catalog/entityActions/labelValue/SetLabelValueEntityAction';
import { ConnectedApplicationEditAction, ToggleConnectedApplicationEnablementAction } from '~src/connectedApplications/actions/ConnectedApplicationActions';
import { AddToContentRelayPlanEntityAction } from '~src/contentRelay/entityActions/AddToContentRelayPlanEntityAction';
import { DeleteContentRelayPlanEntityAction } from '~src/contentRelay/entityActions/DeleteContentRelayPlanEntityAction';
import { RenameContentRelayPlanEntityAction } from '~src/contentRelay/entityActions/RenameContentRelayPlanEntityAction';
import { UpdateDataFreshnessPolicyEntityAction } from '~src/dataFreshness/actions/UpdateDataFreshnessPolicyEntityAction';
import { CopyLuidEntityAction } from '~src/datasource/entityActions/CopyLuidEntityAction';
import { DataAlertAddMeAction } from '~src/dda/entityActions/DataAlertAddMeAction';
import { DataAlertEditAction } from '~src/dda/entityActions/DataAlertEditAction';
import { DataAlertRemoveMeAction } from '~src/dda/entityActions/DataAlertRemoveMeAction';
import { DataAlertResumeAction } from '~src/dda/entityActions/DataAlertResumeAction';
import { ChangeExtractRefreshScheduleEntityAction } from '~src/extractRefresh/entityActions/extractRefresh/ChangeExtractRefreshScheduleEntityAction';
import { DeleteExtractRefreshEntityAction } from '~src/extractRefresh/entityActions/extractRefresh/DeleteExtractRefreshEntityAction';
import { ExtractFailureNotifiationEntityMenu, ExtractFailureNotifiationOptInEntityAction, ExtractFailureNotifiationOptOutEntityAction } from '~src/extractRefresh/entityActions/extractRefresh/ExtractFailureNotificationEntityAction';
import { RefreshExtractsAction } from '~src/extractRefresh/entityActions/extractRefresh/RefreshExtractsAction';
import { ResumeExtractRefreshEntityAction } from '~src/extractRefresh/entityActions/extractRefresh/ResumeExtractRefreshEntityAction';
import {
  RunExtractRefreshEntityAction,
  RunExtractRefreshEntityIfRefreshSSSEnabledAction
} from '~src/extractRefresh/entityActions/extractRefresh/RunExtractRefreshEntityAction';
import { SetExtractRefreshPriorityEntityAction } from '~src/extractRefresh/entityActions/extractRefresh/SetExtractRefreshPriorityEntityAction';
import { ChangeRemoteExtractRefreshEntityAction } from '~src/extractRefresh/entityActions/remoteExtractRefresh/ChangeRemoteExtractRefreshEntityAction';
import { ChangeRemoteExtractRefreshExpectedDurationEntityAction } from '~src/extractRefresh/entityActions/remoteExtractRefresh/ChangeRemoteExtractRefreshExpectedDurationEntityAction';
import { DeleteRemoteExtractRefreshEntityAction } from '~src/extractRefresh/entityActions/remoteExtractRefresh/DeleteRemoteExtractRefreshEntityAction';
import { ScheduleRefreshWithBridgeEntityAction } from '~src/extractRefresh/entityActions/remoteExtractRefresh/ScheduleRefreshWithBridgeEntityAction';
import { DecryptAction } from '~src/extractTask/entityActions/extractEncryption/DecryptAction';
import { EncryptAction } from '~src/extractTask/entityActions/extractEncryption/EncryptAction';
import {
  ExtractsEncryptionAction,
  ExtractsEncryptionEncryptedDsAction,
  ExtractsEncryptionUnencryptedAction
} from '~src/extractTask/entityActions/extractEncryption/ExtractsEncryptionAction';
import { ChangeExtractDsToLiveAction } from '~src/extractTask/entityActions/extractTask/ChangeExtractDsToLiveAction';
import { ChangeExtractEmbeddedDsToLiveAction } from '~src/extractTask/entityActions/extractTask/ChangeExtractEmbeddedDsToLiveAction';
import {
  CreateEncryptedExtractForDsAction,
  CreateExtractForDsAction,
  CreateUnencryptedExtractForDsAction
} from '~src/extractTask/entityActions/extractTask/CreateExtractForDsAction';
import {
  CreateEncryptedExtractForEmbeddedDsAction,
  CreateExtractForEmbeddedDsAction,
  CreateUnencryptedExtractForEmbeddedDsAction
} from '~src/extractTask/entityActions/extractTask/CreateExtractForEmbeddedDsAction';
import { CancelFlowRunEntityAction } from '~src/flow/entityActions/CancelFlowRunEntityAction';
import { CreateFlowSubscriptionEntityAction } from '~src/flow/entityActions/CreateFlowSubscriptionEntityAction';
import { getFlowConnectionEntityAction } from '~src/flow/entityActions/EditFlowConnectionsAction';
import { EditFlowEntityAction } from '~src/flow/entityActions/EditFlowEntityAction';
import { EditFlowTaskAction } from '~src/flow/entityActions/EditFlowTaskAction';
import { NewFlowEntityAction } from '~src/flow/entityActions/NewFlowEntityAction';
import { RunFlowEntityAction } from '~src/flow/entityActions/RunFlowEntityAction';
import { RunFlowTaskChangePriorityAction } from '~src/flow/entityActions/RunFlowTaskChangePriorityAction';
import { RunFlowTaskChangeScheduleAction } from '~src/flow/entityActions/RunFlowTaskChangeScheduleAction';
import { RunFlowTaskResumeAction } from '~src/flow/entityActions/RunFlowTaskResumeAction';
import { RunFlowTaskRunNowAction } from '~src/flow/entityActions/RunFlowTaskRunNowAction';
import { ScheduleFlowTaskSubmenuEntityAction, ScheduleLinkedTaskEntityAction, ScheduleSingleFlowTaskEntityAction } from '~src/flow/entityActions/ScheduleFlowTasksEntityAction';
import { OpenGeocodingConfigDialogEntityAction } from '~src/geocoding/actions/OpenGeocodingConfigDialogEntityAction';
import { GeocodingPermissionHelper } from '~src/geocoding/utils/GeocodingPermissionHelper';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';
import { EditPublishedConnectionEntityAction } from '~src/publishedConnection/entityActions/EditPublishedConnectionEntityAction';
import { PublishedConnectionTableExtractsRefreshNowAction } from '~src/publishedConnectionTable/entityActions/PublishedConnectionTableExtractsRefreshNowAction';
import { ResourceGovernanceEditSiteAction } from '~src/schedule/actions/ResourceGovernanceEditSiteAction';
import { ResourceGovernanceResetSiteToDefaultsAction } from '~src/schedule/actions/ResourceGovernanceResetSiteToDefaultAction';
import { ScheduleDisableAction } from '~src/schedule/actions/ScheduleDisableAction';
import { ScheduleDisableLinkedTaskAction } from '~src/schedule/actions/ScheduleDisableLinkedTaskAction';
import { ScheduleEditAction } from '~src/schedule/actions/ScheduleEditAction';
import { ScheduleEnableAction } from '~src/schedule/actions/ScheduleEnableAction';
import { ScheduleEnableLinkedTaskAction } from '~src/schedule/actions/ScheduleEnableLinkedTaskAction';
import { ScheduleRunNowAction } from '~src/schedule/actions/ScheduleRunNowAction';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { ShareContentAction } from '~src/sharing/entityActions/ShareContentAction';
import { ActivateSiteAction } from '~src/siteManagement/entityActions/ActivateSiteAction';
import { DeleteSiteAction } from '~src/siteManagement/entityActions/DeleteSiteAction';
import { EditSiteAction } from '~src/siteManagement/entityActions/EditSiteAction';
import { SuspendSiteAction } from '~src/siteManagement/entityActions/SuspendSiteAction';
import { DeleteForeverAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/DeleteForeverAction';
import { RestoreAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/RestoreAction';
import { IExplorableItemTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { isAdminProfileSitesSpace, isAdminProfileUsersSpace, isRecycleBinSpace } from '~src/spacesPlatform/state/SpaceInfo';
import { isSubsetOf, KeySet, makeKeySet } from '~src/spacesPlatform/utils/ItemsSet';
import { ChangeSubscriptionDataConditionEntityAction } from '~src/subscription/entityActions/ChangeSubscriptionDataConditionEntityAction';
import { ChangeSubscriptionsFormatEntityAction } from '~src/subscription/entityActions/ChangeSubscriptionsFormatEntityAction';
import { ChangeSubscriptionsFrequencyEntityAction } from '~src/subscription/entityActions/ChangeSubscriptionsFrequencyEntityAction';
import { ChangeSubscriptionSubjectEntityAction } from '~src/subscription/entityActions/ChangeSubscriptionSubjectEntityAction';
import { ResumeSubscriptionEntityAction } from '~src/subscription/entityActions/ResumeSubscriptionEntityAction';
import { UnsubsribeFromSubscriptionsEntityAction } from '~src/subscription/entityActions/UnsubscribeFromSubscriptionsEntityAction';
import { GroupDeleteAction } from '~src/userManagement/groups/entityActions/GroupDeleteAction';
import { GroupEditAction } from '~src/userManagement/groups/entityActions/GroupEditAction';
import { GroupUpdateGrantLicenseModeAction } from '~src/userManagement/groups/entityActions/GroupsUpdateGrantLicenseModeAction';
import { GroupsUpdateMinimumSiteRoleAction } from '~src/userManagement/groups/entityActions/GroupsUpdateMinimumSiteRoleAction';
import { SyncActiveDirectoryGroupsAction } from '~src/userManagement/groups/entityActions/SyncActiveDirectoryGroupsAction';
import { GroupSetDeleteAction } from '~src/userManagement/groupsets/entityActions/GroupSetDeleteAction';
import { GroupSetEditAction } from '~src/userManagement/groupsets/entityActions/GroupSetEditAction';
import { RemoveGroupFromGroupSetAction } from '~src/userManagement/groupsets/entityActions/RemoveGroupsFromGroupSetAction';
import { ServerUserDeleteUsersAction } from '~src/userManagement/users/entityActions/ServerUserDeleteUsersEntityAction';
import { ServerUserEditSiteMembershipUsersAction } from '~src/userManagement/users/entityActions/ServerUserEditSiteMembershipEntityAction';
import { ServerUserForcePasswordupdateAction } from '~src/userManagement/users/entityActions/ServerUserForcePasswordUpdateEntityAction';
import { ServerUserResetLoginRateLimitingAction } from '~src/userManagement/users/entityActions/ServerUserResetLoginRateLimitingAction';
import { SiteUserEditGroupMembershipAction } from '~src/userManagement/users/entityActions/SiteUserEditGroupMembershipEntityAction';
import { SiteUserForcePasswordUpdateAction } from '~src/userManagement/users/entityActions/SiteUserForcePasswordUpdateEntityAction';
import { SiteUserRemoveAction } from '~src/userManagement/users/entityActions/SiteUserRemoveEntityAction';
import { SiteUserRemoveFromGroupAction } from '~src/userManagement/users/entityActions/SiteUserRemoveFromGroupEntityAction';
import { SiteUserResetLoginRateLimitingAction } from '~src/userManagement/users/entityActions/SiteUserResetLoginRateLimitingEntityAction';
import { SiteUserResetMFAAction } from '~src/userManagement/users/entityActions/SiteUserResetMFAEntityAction';
import { SiteUserSetAuthenticationAction } from '~src/userManagement/users/entityActions/SiteUserSetAuthenticationEntityAction';
import { SiteUserSiteRoleAction } from '~src/userManagement/users/entityActions/SiteUserSiteRoleEntityAction';
import { EditViewAction } from '~src/views/components/EditViewAction';
import { EditWorkbookAction } from '~src/workbook/entityActions/EditWorkbookAction';
import { NewWorkbookEntityAction } from '~src/workbook/entityActions/NewWorkbookEntityAction';
import { OpenWorkbookInDesktopAction } from '~src/workbook/entityActions/OpenWorkbookInDesktopAction';

import { ExplorableItemType } from '../../models/ContentType';
import { AcknowledgeUserIdentityConflictAction } from './EntityActions/AcknowledgeUserIdentityConflictAction';
import { CollectionDetailsAction } from './EntityActions/CollectionDetailsAction';
import { DeleteAction } from './EntityActions/DeleteAction';
import { DownloadEntityAction } from './EntityActions/download/DownloadEntityAction';
import { EditDataConnectionsAction } from './EntityActions/EditDataConnectionAction';
import { EditDatasourceEntityAction } from './EntityActions/EditDatasourceEntityAction';
import { AddFavoriteAction, RemoveFavoriteAction } from './EntityActions/FavoriteAction';
import { GeocodeDatasourceAction } from './EntityActions/GeocodeDatasourceAction';
import { HideRecommendationAction } from './EntityActions/HideRecommendationAction';
import { IEntityAction } from './EntityActions/IEntityAction';
import { MoveAction } from './EntityActions/MoveAction';
import { OpenManageItemsInCollectionsDialogEntityAction } from './EntityActions/OpenManageItemsInCollectionsDialog';
import { PublishToSalesforceAction } from './EntityActions/PublishToSalesforceAction';
import { ReevaluateUserIdentityConflictAction } from './EntityActions/ReevaluateUserIdentityConflictAction';
import { RemoveFromThisCollectionAction } from './EntityActions/RemoveFromThisCollectionAction';
import {
  RenameCollectionAction,
  RenameDatasourceAction,
  RenameEntityAction,
  RenameGroupAction
} from './EntityActions/RenameEntityAction';
import { ResolveAmbiguousUserIdentityConflictAction } from './EntityActions/ResolveAmbiguousUserIdentityConflictAction';
import { RetryUserIdentityMigrationAction } from './EntityActions/RetryUserIdentityMigrationActions';
import { RevisionHistoryEntityAction } from './EntityActions/RevisionHistoryEntityAction';
import { SetCollectionOwnerEntityAction } from './EntityActions/SetCollectionOwnerAction';
import { SetContactAction } from './EntityActions/SetContactAction';
import {
  SetContentRelayPlanDescriptionAction,
  SetDescriptionAction,
  SetPublishedConnectionDescriptionAction,
  SetPublishedConnectionTableDescriptionAction
} from './EntityActions/SetDescriptionAction';
import { SetDisplayTabsAction } from './EntityActions/SetDisplayTabsAction';
import { SetOwnerAction } from './EntityActions/SetOwnerAction';
import { SetTagsAction } from './EntityActions/SetTagsAction';
import { WhoHasSeenThisViewEntityAction } from './EntityActions/WhoHasSeenThisViewEntityAction';


interface IEntityActionListEntry<T extends ExplorableItemType> {
  action: IEntityAction<IExplorableItemTypeInfo<T>, any>;
  supportsMultipleItems: boolean;
  contentTypes: KeySet<T>;
  subEntityActionList?: IEntityActionListEntry<T>[];
  supportedInRecycleBin?: boolean;
  onlyInAdminProfileSitesSpace?: boolean;
  onlyInAdminProfileUsersSpace?: boolean;
  resourcePermission?: { resourceType: string, resourceActions: string[] };
}

interface ContentActionsSectionsProps {
  linkedTaskSchedulingEnabled: boolean;
  databasesInProjectsEnabled: boolean;
  tablesInProjectsEnabled: boolean;
  suggestDescriptionsEnabled: boolean;
  catalogEnabled: boolean;
  geocodingEnabled: boolean;
  workbookLabelEnabled: boolean;
  aiLabelEnabled: boolean;
  aiLabelForDatasourceEnabled: boolean;
}

/**
 * This function does nothing useful at runtime, but it enforces compile-time self-consistency of the
 * type parameter for each list entry, particularly that the `action` and `contentTypes` are both
 * applicable to the same values of the type parameter T, since we can't achieve that specificity
 * in the array type declaration for ContentActionsSections.
 */
function validEntry<T extends ExplorableItemType>(entry: IEntityActionListEntry<T>): IEntityActionListEntry<T> {
  return entry;
}

function getAiLabelContentTypes(
  aiLabelEnabled: boolean,
  aiLabelForDatasourceEnabled: boolean
): KeySet<'workbook' | 'datasource'> {
  if (!aiLabelEnabled) {
    return makeKeySet();
  }
  if (aiLabelForDatasourceEnabled) {
    return makeKeySet('workbook', 'datasource');
  }
  return makeKeySet('workbook');
}

/**
 * This is a list of all the entity actions that can appear in actions menus (in explorers or mastheads).
 * The top level arrays denote "sections" separated in the menu by a horizontal divider.
 *
 * When the actions menu is displayed, this list gets filtered to show only the actions that apply to the selected items.
 *
 * If the entity actions should change based on Feature Flag or based on any value that can be overridden at runtime,
 * such value must be passed as parameter for the entity actions to be evaluated correctly.
 */
const ContentActionsSections = (props: ContentActionsSectionsProps): IEntityActionListEntry<ExplorableItemType>[][] => [
  [
    validEntry({ action: AddFavoriteAction, supportsMultipleItems: false, contentTypes: makeKeySet('view', 'collection', 'datasource', 'flow', 'project', 'virtualConnection', 'virtualConnectionTable','workbook') }),
    validEntry({ action: RemoveFavoriteAction, supportsMultipleItems: false, contentTypes: makeKeySet('view', 'collection', 'datasource', 'flow', 'project', 'virtualConnection', 'virtualConnectionTable','workbook') }),
  ],
  [
    validEntry({ action: EditDatasourceEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
  ],
  [
    validEntry({ action: NewWorkbookEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: NewFlowEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: EditFlowEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('flow') }),
    validEntry({ action: RunFlowEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('flow') }),
    validEntry({
      action: ScheduleFlowTaskSubmenuEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('flow'),
      subEntityActionList: [
        validEntry({ action: ScheduleSingleFlowTaskEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('flow') }),
        validEntry({ action: ScheduleLinkedTaskEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('flow') }),
      ]
    }),
  ],
  [
    validEntry({ action: CancelFlowRunEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('flowOutputStepRun') }),
  ],
  [
    validEntry({ action: SiteUserEditGroupMembershipAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser'),
      resourcePermission: { resourceType: 'Group', resourceActions: ['CHANGE_GROUP_MEMBERSHIP'] } }),
    validEntry({ action: SiteUserSiteRoleAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser'),
      resourcePermission: { resourceType: 'User', resourceActions: ['CHANGE_SITE_ROLE'] } }),
    validEntry({ action: SiteUserSetAuthenticationAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser') }),
    validEntry({ action: SiteUserResetMFAAction, supportsMultipleItems: false, contentTypes: makeKeySet('siteuser') }),
    validEntry({ action: SiteUserRemoveAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser'),
      resourcePermission: { resourceType: 'User', resourceActions: ['REMOVE_FROM_SITE'] } }),
    validEntry({ action: SiteUserForcePasswordUpdateAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser'),
      resourcePermission: { resourceType: 'User', resourceActions: ['FORCE_PASSWORD_UPDATE'] } }),
    validEntry({ action: SiteUserResetLoginRateLimitingAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser'),
      resourcePermission: { resourceType: 'User', resourceActions: ['UNLOCK_ACCOUNT_ACCESS'] } }),
    validEntry({ action: SiteUserRemoveFromGroupAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser'),
      resourcePermission: { resourceType: 'Group', resourceActions: ['CHANGE_GROUP_MEMBERSHIP'] } }),
    validEntry({ action: SiteUserAddToIdentityPoolAction, supportsMultipleItems: false, contentTypes: makeKeySet('siteuser') }),
    validEntry({ action: SiteUserRemoveFromIdentityPoolAction, supportsMultipleItems: false, contentTypes: makeKeySet('siteuser') })
  ],
  [
    validEntry({ action: SyncActiveDirectoryGroupsAction, supportsMultipleItems: true, contentTypes: makeKeySet('group'),
      resourcePermission: { resourceType: 'Group', resourceActions: ['CREATE_GROUP'] } }),
    validEntry({ action: RenameGroupAction, supportsMultipleItems: false, contentTypes: makeKeySet('group'),
      resourcePermission: { resourceType: 'Group', resourceActions: ['MANAGE_GROUPS'] } }),
    validEntry({ action: GroupsUpdateMinimumSiteRoleAction, supportsMultipleItems: true, contentTypes: makeKeySet('group'),
      resourcePermission: { resourceType: 'Group', resourceActions: ['MANAGE_GROUPS'] } }),
    validEntry({ action: GroupUpdateGrantLicenseModeAction, supportsMultipleItems: true, contentTypes: makeKeySet('group'),
      resourcePermission: { resourceType: 'Group', resourceActions: ['MANAGE_GROUPS'] } }),
    validEntry({ action: GroupDeleteAction, supportsMultipleItems: true, contentTypes: makeKeySet('group'),
      resourcePermission: { resourceType: 'Group', resourceActions: ['DELETE_GROUP'] } }),
    validEntry({ action: GroupEditAction, supportsMultipleItems: false, contentTypes: makeKeySet('group'),
      resourcePermission: { resourceType: 'Group', resourceActions: ['MANAGE_GROUPS'] } })
  ],
  [
    validEntry({ action: GroupSetDeleteAction, supportsMultipleItems: true, contentTypes: makeKeySet('groupset') }),
    validEntry({ action: GroupSetEditAction, supportsMultipleItems: false, contentTypes: makeKeySet('groupset') }),
    validEntry({ action: RemoveGroupFromGroupSetAction, supportsMultipleItems: true, contentTypes: makeKeySet('group') }),
  ],
  [
    validEntry({ action: ServerUserEditSiteMembershipUsersAction, supportsMultipleItems: true, contentTypes: makeKeySet('serveruser') }),
    validEntry({ action: ServerUserDeleteUsersAction, supportsMultipleItems: true, contentTypes: makeKeySet('serveruser') }),
    validEntry({ action: ServerUserForcePasswordupdateAction, supportsMultipleItems: true, contentTypes: makeKeySet('serveruser') }),
    validEntry({ action: ServerUserResetLoginRateLimitingAction, supportsMultipleItems: true, contentTypes: makeKeySet('serveruser') }),
    validEntry({ action: ServerUserAddToIdentityPoolAction, supportsMultipleItems: false, contentTypes: makeKeySet('serveruser') }),
    validEntry({ action: ServerUserRemoveFromIdentityPoolAction, supportsMultipleItems: false, contentTypes: makeKeySet('serveruser') })
  ],
  [
    validEntry({ action: EditPublishedConnectionEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('virtualConnection') }),
    validEntry({ action: EditWorkbookAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook') }),
    validEntry({ action: EditViewAction, supportsMultipleItems: false, contentTypes: makeKeySet('view') }),
    validEntry({ action: OpenWorkbookInDesktopAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'view') }),
  ],
  [
    validEntry({ action: ToggleConnectedApplicationEnablementAction, supportsMultipleItems: false, contentTypes: makeKeySet('connectedApplication')}),
    validEntry({ action: ConnectedApplicationEditAction, supportsMultipleItems: false, contentTypes: makeKeySet('connectedApplication')})
  ],
  [
    validEntry({ action: ShareContentAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'project', 'view', 'datasource', 'flow', 'recommendedView', 'virtualConnection', 'collection') }),
    validEntry({ action: CreateFlowSubscriptionEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('flow') }),
    validEntry({ action: DownloadEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'datasource', 'flow') }),
    validEntry({ action: HideRecommendationAction, supportsMultipleItems: false, contentTypes: makeKeySet('recommendedView') }),
    validEntry({ action: PublishToSalesforceAction, supportsMultipleItems: true, contentTypes: makeKeySet('view') }),
  ],
  [
    validEntry({
      action: OpenManageItemsInCollectionsDialogEntityAction, supportsMultipleItems: true,
      contentTypes: makeKeySet('workbook', 'datasource', 'view', 'flow', 'project', 'virtualConnection', 'virtualConnectionTable' /*, 'table', 'database' TFS 1089949*/)
    }),
    validEntry({
      action: AddToContentRelayPlanEntityAction, supportsMultipleItems: true,
      // Every type the client can relay (the keys of relaySelection's wire mapping; keep in sync). The
      // action's isClickable narrows it to the server's currently-supported set (listSupportedContentTypes).
      contentTypes: makeKeySet('project', 'workbook', 'datasource')
    }),
    validEntry({
      action: RemoveFromThisCollectionAction, supportsMultipleItems: true,
      contentTypes: makeKeySet('workbook', 'datasource', 'view', 'flow', 'project', 'virtualConnection', 'virtualConnectionTable' /*, 'table', 'database' TFS 1089949*/)
    }),
  ],
  [
    validEntry({ action: DataAlertAddMeAction, supportsMultipleItems: true, contentTypes: makeKeySet('dataalert') }),
    validEntry({ action: DataAlertRemoveMeAction, supportsMultipleItems: true, contentTypes: makeKeySet('dataalert') }),
    validEntry({ action: DataAlertResumeAction, supportsMultipleItems: false, contentTypes: makeKeySet('dataalert') }),
    validEntry({ action: DataAlertEditAction, supportsMultipleItems: false, contentTypes: makeKeySet('dataalert') }),
  ],
  [
    validEntry({ action: ScheduleRunNowAction, supportsMultipleItems: true, contentTypes: makeKeySet('schedule') }),
    validEntry({ action: ScheduleEnableAction, supportsMultipleItems: true, contentTypes: makeKeySet('schedule') }),
    validEntry({ action: ScheduleDisableAction, supportsMultipleItems: true, contentTypes: makeKeySet('schedule') }),
    validEntry({ action: ScheduleEditAction, supportsMultipleItems: false, contentTypes: makeKeySet('schedule') }),
  ].concat(
    areLinkedTasksEnabled() ?
      [
        validEntry({ action: ScheduleEnableLinkedTaskAction, supportsMultipleItems: true, contentTypes: makeKeySet('schedule') }),
        validEntry({ action: ScheduleDisableLinkedTaskAction, supportsMultipleItems: true, contentTypes: makeKeySet('schedule') }),
      ] : []),
  [
    validEntry({ action: SetTagsAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource', 'view', 'flow', 'virtualConnection', 'virtualConnectionTable', 'database', 'table') }),
    validEntry({ action: RenameEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'project', 'flow', 'schedule', 'virtualConnection') }),
    validEntry({ action: MoveAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'project', 'datasource', 'flow', 'virtualConnection'
        ,...props.tablesInProjectsEnabled ? ['table']:[],  ... props.databasesInProjectsEnabled ? ['database']: []) }),
    validEntry({ action: RenameDatasourceAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),

    validEntry({ action: SetDescriptionAction, supportsMultipleItems: false, contentTypes: makeKeySet('database', 'table', 'datasource', 'workbook') }),
    validEntry({ action: SetPublishedConnectionDescriptionAction, supportsMultipleItems: false, contentTypes: makeKeySet('virtualConnection') }),
    validEntry({ action: SetPublishedConnectionTableDescriptionAction, supportsMultipleItems: false, contentTypes: makeKeySet('virtualConnectionTable') }),

    // Set Permissions can be performed on multiple items of the same type, but not multiple items of different types,
    // so we're representing it as separate list entries to facilitate that.
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('project') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('view') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('flow') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('table') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('database') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('virtualConnection') }),

    validEntry({ action: WhoHasSeenThisViewEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('view') }),

    validEntry({ action: SetOwnerAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'project', 'datasource', 'flow', 'dataalert', 'virtualConnection', 'customView') }),
    validEntry({ action: SetContactAction, supportsMultipleItems: false, contentTypes: makeKeySet('database', 'table') }),
    validEntry({ action: GroupDatabasesEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('database') }),
    validEntry({ action: UngroupDatabasesEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('database') }),
    validEntry({ action: CopyLuidEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
  ],
  (
    props.catalogEnabled ?
    [
      validEntry({
        action: DataLabelsSubmenu, supportsMultipleItems: false, contentTypes: makeKeySet('database', 'table', 'virtualConnection', 'virtualConnectionTable', 'datasource', 'flow', 'workbook'),
        subEntityActionList: [
          validEntry({ action: SetCertificationEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource', 'virtualConnection', 'virtualConnectionTable') }),
          validEntry({ action: SetCertificationDatabaseAndTableAction, supportsMultipleItems: false, contentTypes: makeKeySet('database', 'table') }),
          validEntry({ action: SetDataQualityEntityAction, supportsMultipleItems: false, contentTypes: props.workbookLabelEnabled ? makeKeySet('datasource', 'flow', 'workbook') : makeKeySet('datasource', 'flow') }),
          validEntry({ action: DataQualityMenu, supportsMultipleItems: false, contentTypes: makeKeySet('database', 'table', 'virtualConnection', 'virtualConnectionTable') }),
          validEntry({ action: SetSensitiveLabelEntityAction, supportsMultipleItems: false, contentTypes: props.workbookLabelEnabled ? makeKeySet('datasource', 'flow', 'virtualConnection', 'virtualConnectionTable', 'database', 'table', 'workbook') : makeKeySet('datasource', 'flow', 'virtualConnection', 'virtualConnectionTable', 'database', 'table') }),
          validEntry({ action: SetAiLabelEntityAction, supportsMultipleItems: false, contentTypes: getAiLabelContentTypes(props.aiLabelEnabled, props.aiLabelForDatasourceEnabled) }),
          validEntry({ action: SetCustomLabelEntityAction, supportsMultipleItems: false, contentTypes: FeatureFlags.customLabelCategoriesEnabled ? (props.workbookLabelEnabled ? makeKeySet('datasource', 'flow', 'virtualConnection', 'virtualConnectionTable', 'database', 'table', 'workbook') : makeKeySet('datasource', 'flow', 'virtualConnection', 'virtualConnectionTable', 'database', 'table')): makeKeySet() }),
          validEntry({ action: SetDataQualityTriggerAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource', 'flow') }),
        ]
      }),
      validEntry({ action: SetLabelValueEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('labelValue') }),
      validEntry({ action: DeleteLabelValueEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('labelValue')}),
      validEntry({ action: RevertToDefaultLabelValueEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('labelValue') }),
    ] : [
      validEntry({ action: SetCertificationEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') })
    ]
  ),
  [
    validEntry({ action: RemoveExternalContentEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('database', 'table') }),
  ],
  (
    props.geocodingEnabled ?
    [
      validEntry({ action: GeocodeDatasourceAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') }),
    ] : []
  ),
  [
    validEntry({ action: EditDataConnectionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource', 'dataconnection') }),
    validEntry({ action: SetDisplayTabsAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook') }),
    validEntry({ action: ChangeExtractDsToLiveAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
    validEntry({
      action: CreateExtractForDsAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource'),
      subEntityActionList: [
        validEntry({ action: CreateEncryptedExtractForDsAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
        validEntry({ action: CreateUnencryptedExtractForDsAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') })
      ]
    }),
    validEntry({ action: ChangeExtractEmbeddedDsToLiveAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') }),
    validEntry({
      action: CreateExtractForEmbeddedDsAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource'),
      subEntityActionList: [
        validEntry({ action: CreateEncryptedExtractForEmbeddedDsAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') }),
        validEntry({ action: CreateUnencryptedExtractForEmbeddedDsAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') })
      ]
    }),
    validEntry({
      action: ExtractsEncryptionAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook'),
      subEntityActionList: [
        validEntry({ action: ExtractsEncryptionEncryptedDsAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook') }),
        validEntry({ action: ExtractsEncryptionUnencryptedAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook') })
      ]
    }),
    validEntry({ action: RefreshExtractsAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource') }),
    validEntry({ action: PublishedConnectionTableExtractsRefreshNowAction, supportsMultipleItems: true, contentTypes: makeKeySet('virtualConnectionTable') }),
    validEntry({ action: RunExtractRefreshEntityIfRefreshSSSEnabledAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource') }),
    validEntry({ action: ScheduleRefreshWithBridgeEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: EncryptAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource', 'flow') }),
    validEntry({ action: DecryptAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource', 'flow') }),
    validEntry({
      action: ExtractFailureNotifiationEntityMenu, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource'),
      subEntityActionList: [
        validEntry({ action: ExtractFailureNotifiationOptInEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource') }),
        validEntry({ action: ExtractFailureNotifiationOptOutEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource') })
      ]
    }),
  ],
  [
    validEntry({ action: RunFlowTaskRunNowAction, supportsMultipleItems: true, contentTypes: makeKeySet('runFlowTask') }),
  ],
  [
    validEntry({ action: EditFlowTaskAction, supportsMultipleItems: false, contentTypes: makeKeySet('runFlowTask') }),
    validEntry({ action: RunFlowTaskChangeScheduleAction, supportsMultipleItems: true, contentTypes: makeKeySet('runFlowTask') }),
    validEntry({ action: RunFlowTaskChangePriorityAction, supportsMultipleItems: true, contentTypes: makeKeySet('runFlowTask') }),
    validEntry({ action: RunFlowTaskResumeAction, supportsMultipleItems: true, contentTypes: makeKeySet('runFlowTask') }),
  ],
  [
    validEntry({ action: CollectionDetailsAction, supportsMultipleItems: false, contentTypes: makeKeySet('collection') }),
    validEntry({ action: RenameCollectionAction, supportsMultipleItems: false, contentTypes: makeKeySet('collection') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('collection') }),
    validEntry({ action: SetCollectionOwnerEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('collection') }),
  ],
  [
    validEntry({ action: SetContentRelayPlanDescriptionAction, supportsMultipleItems: false, contentTypes: makeKeySet('contentRelayPlan') }),
    validEntry({ action: RenameContentRelayPlanEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('contentRelayPlan') }),
    validEntry({ action: DeleteContentRelayPlanEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('contentRelayPlan') }),
  ],
  [
    validEntry({ action: RevisionHistoryEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'datasource', 'flow', 'virtualConnection') }),
    validEntry({ action: DeleteAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'project', 'datasource', 'view', 'flow', 'dataalert', 'schedule', 'runFlowTask', 'virtualConnection', 'collection', 'customView', 'connectedApplication') })
  ],
  [
    validEntry({ action: ResumeSubscriptionEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('subscription') }),
    validEntry({ action: ChangeSubscriptionsFrequencyEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('subscription') }),
    validEntry({ action: ChangeSubscriptionSubjectEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('subscription') }),
    validEntry({ action: ChangeSubscriptionDataConditionEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('subscription') }),
    validEntry({ action: ChangeSubscriptionsFormatEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('subscription') }),
    validEntry({ action: UnsubsribeFromSubscriptionsEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('subscription') }),
  ],
  [
    validEntry({ action: RunExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: ResumeExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: ChangeExtractRefreshScheduleEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: SetExtractRefreshPriorityEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: DeleteExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
  ],
  [
    validEntry({ action: ResolveAmbiguousUserIdentityConflictAction, supportsMultipleItems: false, contentTypes: makeKeySet('userIdentityConflict') }),
    validEntry({ action: RetryUserIdentityMigrationAction, supportsMultipleItems: true, contentTypes: makeKeySet('userIdentityConflict') }),
    validEntry({ action: AcknowledgeUserIdentityConflictAction, supportsMultipleItems: true, contentTypes: makeKeySet('userIdentityConflict') }),
    validEntry({ action: ReevaluateUserIdentityConflictAction, supportsMultipleItems: true, contentTypes: makeKeySet('userIdentityConflict') }),
  ],
  [
    validEntry({ action: DeleteRemoteExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('remoteRefreshSchedule') }),
    validEntry({ action: ChangeRemoteExtractRefreshEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('remoteRefreshSchedule') }),
    validEntry({ action: ChangeRemoteExtractRefreshExpectedDurationEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('remoteRefreshSchedule') }),
  ],
  [
    validEntry({ action: CancelBackgroundJobAction, supportsMultipleItems: false, contentTypes: makeKeySet('backgroundJob') }),
    validEntry({ action: ViewBackgroundJobDetailsAction, supportsMultipleItems: false, contentTypes: makeKeySet('backgroundJob') }),
  ],
  [
    validEntry({ action: AdminProfileRemoveFromSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('site'),
      onlyInAdminProfileSitesSpace: true }),
  ],
  [
    validEntry({ action: AdminProfileSiteMembershipAction, supportsMultipleItems: true, contentTypes: makeKeySet('serveruser'),
      onlyInAdminProfileUsersSpace: true }),
    validEntry({ action: AdminProfileRemoveFromUserAction, supportsMultipleItems: true, contentTypes: makeKeySet('serveruser'),
      onlyInAdminProfileUsersSpace: true }),
  ],
  [
    validEntry({ action: ActivateSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('site') }),
    validEntry({ action: SuspendSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('site') }),
    validEntry({ action: EditSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('site') }),
    validEntry({ action: DeleteSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('site') }),
  ],
  [
    validEntry({ action: UpdateDataFreshnessPolicyEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook') }),
  ],
  [
    validEntry({ action: ResourceGovernanceResetSiteToDefaultsAction, supportsMultipleItems: true, contentTypes: makeKeySet('resourceGovernanceSetting') }),
    validEntry({ action: ResourceGovernanceEditSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('resourceGovernanceSetting') }),
  ],
  [
    validEntry({ action: OpenGeocodingConfigDialogEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('addressGeocodingTask') }),
  ],
  [
    validEntry({ action: RestoreAction, supportsMultipleItems: true, supportedInRecycleBin: true, contentTypes: makeKeySet('workbook', 'project', 'datasource', 'flow') })
  ],
  [
    validEntry({ action: DeleteForeverAction, supportsMultipleItems: true, supportedInRecycleBin: true, contentTypes: makeKeySet('workbook', 'project', 'datasource', 'flow') })
  ]
];

const memoizedContentActionsSections = defaultMemoize(ContentActionsSections);

export function getActionSectionsForItems(...items: IExplorableItemTypeInfo[]): IEntityAction<IExplorableItemTypeInfo, any>[][] {
  const state = getAppStore().getState();
  const itemTypes = makeKeySet(...items.map(item => item.contentType));
  const contentActionSectionsProps: ContentActionsSectionsProps = {
    linkedTaskSchedulingEnabled: areLinkedTasksEnabled(),
    databasesInProjectsEnabled: FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled,
    tablesInProjectsEnabled: getSessionManagementServiceInstance().tablesInProjectsUIEnabled(),
    suggestDescriptionsEnabled: FeatureFlags.suggestDescriptionsEnabled,
    catalogEnabled: SessionSiteSelectors.catalogingOnSiteEnabled(state),
    geocodingEnabled: GeocodingPermissionHelper.isGeocodingEnabledOnSite(),
    workbookLabelEnabled: FeatureFlags.workbookLabelEnabled,
    aiLabelEnabled: FeatureFlags.aiLabelEnabled,
    aiLabelForDatasourceEnabled: FeatureFlags.aiLabelForDatasourceEnabled,
  };
  const sections = memoizedContentActionsSections(contentActionSectionsProps);

  const entityActionSections = sections.map(section => {
    const eligibleEntries = [].concat(section, _.flatten(section.filter(info => !!info.subEntityActionList).map(info => info.subEntityActionList.map(subEntity => {
      subEntity.action.parentName = info.action.name;
      return subEntity;
    }))))
      .filter(info => isSubsetOf(itemTypes, info.contentTypes))
      .filter(info => info.supportsMultipleItems || items.length === 1)
      .filter(info => !isRecycleBinSpace(state.spaceInfo) === !info.supportedInRecycleBin)
      .filter(info => !isAdminProfileSitesSpace(state.spaceInfo) === !info.onlyInAdminProfileSitesSpace)
      .filter(info => !isAdminProfileUsersSpace(state.spaceInfo) === !info.onlyInAdminProfileUsersSpace);

    const submenuParentsWithChildren: { [parentName: string]: true } = {};
    eligibleEntries.forEach(info => {
      if (info.action.parentName) {
        submenuParentsWithChildren[info.action.parentName] = true;
      }
    });

    return eligibleEntries
      .filter(info => !info.subEntityActionList || submenuParentsWithChildren[info.action.name])
      .map(info => {
        if (info.resourcePermission) {
          info.action.resourcePermission = info.resourcePermission;
        }
        return getFlowConnectionEntityAction(info.action, items);
      });
  });

  return entityActionSections.filter(section => !_(section).isEmpty());
}
